/**
 * CreateShiftModal — extracted from modals.js on Feb 20, 2026.
 * The largest single sub-component in group-homes (707L). Split out so
 * modals.js drops below 1,350 lines and this heavy modal can eventually
 * be `React.lazy()`-loaded.
 */
import React, { useState, useEffect, useRef } from 'react';
import { apiCall } from '../../../utils/api';
import { truncateLabel } from '../../../utils/text';
import { toast } from '../../ui/sonner';
import { validateTimeRange } from '../../../utils/validation';
import PayRateGate from '../../shared/PayRateGate';
import {
  AlertCircle, Camera, CheckCircle, ClipboardList, Pen, Plus, RefreshCw,
  Trash2, Type, X
} from 'lucide-react';

function CreateShiftModal({ programs, selectedProgram, selectedLocation, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({
    program_id: selectedProgram?._id || '',
    location_id: selectedLocation?._id || '',
    title: '',
    description: '',
    pay_rate: 25,
    start_date: '',
    start_time: '09:00',
    end_date: '',
    end_time: '17:00',
    required_workers: 1,
    urgent: false,
    certifications_required: [],
    // Shift Tasks
    shift_tasks: [],
    allow_photo_uploads: false,
    // Task Verification
    task_verification: {
      manager_name: '',
      manager_signature: '',
      requirement_accepted: null,
      verified_at: null
    }
  });
  
  // Task input state
  const [taskInput, setTaskInput] = useState('');
  
  // E-Signature states
  const [signatureMode, setSignatureMode] = useState('type');
  const [typedSignature, setTypedSignature] = useState('');
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
  
  // Canvas drawing functions
  const getCanvasCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    const { x, y } = getCanvasCoordinates(e, canvas);
    setIsDrawing(true);
    setLastPos({ x, y });
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoordinates(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.x, lastPos.y);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1e40af';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    setLastPos({ x, y });
  };

  const stopDrawing = () => {
    if (isDrawing && canvasRef.current) {
      const signatureData = canvasRef.current.toDataURL();
      setForm(prev => ({
        ...prev,
        task_verification: { ...prev.task_verification, manager_signature: signatureData, requirement_accepted: null }
      }));
    }
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setForm(prev => ({
      ...prev,
      task_verification: { ...prev.task_verification, manager_signature: '', requirement_accepted: null }
    }));
  };
  
  // Add task handler
  const handleAddTask = () => {
    if (taskInput.trim()) {
      setForm(prev => ({
        ...prev,
        shift_tasks: [...prev.shift_tasks, { id: Date.now(), text: taskInput.trim(), completed: false }]
      }));
      setTaskInput('');
    }
  };
  
  // Remove task handler
  const handleRemoveTask = (taskId) => {
    setForm(prev => ({
      ...prev,
      shift_tasks: prev.shift_tasks.filter(t => t.id !== taskId)
    }));
  };

  useEffect(() => {
    if (form.program_id) {
      loadLocations(form.program_id);
    }
  }, [form.program_id]);

  const loadLocations = async (programId) => {
    try {
      const response = await apiCall(`/api/employer/group-homes/programs/${programId}/locations`);
      setLocations(response.locations || []);
    } catch (error) {
      console.error('Failed to load locations:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.program_id || !form.location_id) {
      toast.error('Please select program and location');
      return;
    }
    // Combine date and time for backend
    const submitData = {
      ...form,
      start_time: form.start_date && form.start_time ? `${form.start_date}T${form.start_time}` : '',
      end_time: form.end_date && form.end_time ? `${form.end_date}T${form.end_time}` : ''
    };
    const rangeErr = validateTimeRange(submitData.start_time, submitData.end_time, 'Shift end time');
    if (rangeErr) {
      toast.error(rangeErr);
      return;
    }
    delete submitData.start_date;
    delete submitData.end_date;
    
    setLoading(true);
    try {
      await apiCall('/api/employer/group-homes/shifts', {
        method: 'POST',
        body: JSON.stringify(submitData)
      });
      toast.success('Shift posted successfully!');
      onCreated();
    } catch (error) {
      toast.error(error.message || 'Failed to create shift');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 sticky top-0 bg-white">
          <h3 className="text-xl font-bold text-slate-800">Post New Shift</h3>
          <p className="text-sm text-slate-500">Create staffing request for job site</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Program *</label>
              <select
                value={form.program_id}
                onChange={(e) => setForm({ ...form, program_id: e.target.value, location_id: '' })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-amber-500"
                required
                data-testid="group-homes-create-shift-program"
              >
                <option value="">Select Program</option>
                {programs.map(p => (
                  <option key={p._id} value={p._id} title={p.name}>{truncateLabel(p.name, 48)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Location *</label>
              <select
                value={form.location_id}
                onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-amber-500 disabled:bg-slate-100 disabled:text-slate-400"
                required
                disabled={!form.program_id}
                data-testid="group-homes-create-shift-location"
              >
                <option value="">
                  {!form.program_id ? 'Pick a program first' :
                    locations.length === 0 ? 'No locations yet — add one first' :
                    'Select Location'}
                </option>
                {locations.map(l => {
                  // iter77: surface the address inside the option label so the
                  // dropdown is scannable and users don't wonder which house
                  // "Sunrise" refers to.
                  const a = l.address;
                  let addr = '';
                  if (a && typeof a === 'object') {
                    addr = [a.street, a.city, a.state, a.zip].filter(Boolean).join(', ');
                  } else if (typeof a === 'string') {
                    addr = a;
                  }
                  return (
                    <option key={l._id} value={l._id} title={`${l.name}${addr ? ` — ${addr}` : ''}`}>
                      {truncateLabel(l.name, 40)}{addr ? ` — ${truncateLabel(addr, 42)}` : ''}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Shift Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-amber-500"
              placeholder="e.g., Night Shift Caregiver"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-amber-500 resize-none"
              rows={2}
              placeholder="Describe the shift requirements..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Start Date *</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-amber-500"
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Start Time *</label>
              <input
                type="time"
                value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-amber-500"
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">End Date *</label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-amber-500"
                min={form.start_date || new Date().toISOString().split('T')[0]}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">End Time *</label>
              <input
                type="time"
                value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-amber-500"
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Pay Rate ($/hr) *</label>
              <PayRateGate>
              <input
                type="number"
                value={form.pay_rate}
                onChange={(e) => setForm({ ...form, pay_rate: parseFloat(e.target.value) })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-amber-500 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                min="1"
                step="0.01"
                required
              />
              </PayRateGate>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Workers Needed *</label>
              <input
                type="number"
                value={form.required_workers}
                onChange={(e) => setForm({ ...form, required_workers: parseInt(e.target.value) })}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-amber-500"
                min="1"
                required
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="urgent"
              checked={form.urgent}
              onChange={(e) => setForm({ ...form, urgent: e.target.checked })}
              className="w-5 h-5 rounded border-slate-300 text-amber-600"
            />
            <label htmlFor="urgent" className="text-sm text-slate-700">Mark as urgent</label>
          </div>

          {/* ========== SHIFT TASKS SECTION ========== */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <h4 className="font-medium text-slate-800 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-indigo-600" />
                Shift Tasks
              </h4>
              <p className="text-sm text-slate-500 mt-1">Add tasks that workers must complete during this shift. Workers will check off completed tasks.</p>
            </div>
            <div className="p-4 space-y-3">
              {/* Add Task Input */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={taskInput}
                  onChange={(e) => setTaskInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTask())}
                  className="flex-1 px-4 py-3 border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter a task (e.g., Complete patient rounds, Document vitals...)"
                />
                <button
                  type="button"
                  onClick={handleAddTask}
                  className="px-4 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>

              {/* Task List */}
              {form.shift_tasks.length > 0 ? (
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                    <p className="text-sm font-medium text-slate-700">Tasks to Complete ({form.shift_tasks.length})</p>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {form.shift_tasks.map((task, index) => (
                      <div key={task.id} className="flex items-center gap-3 p-4 hover:bg-slate-50">
                        <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-medium flex-shrink-0">
                          {index + 1}
                        </span>
                        <span className="flex-1 text-slate-700">{task.text}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveTask(task.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
                  <p className="text-slate-500 text-sm">No tasks added yet. Add tasks above that workers need to complete during this shift.</p>
                </div>
              )}
            </div>
          </div>

          {/* ========== ALLOW PHOTO UPLOADS ========== */}
          <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-200">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="allow_photo_uploads"
                checked={form.allow_photo_uploads || false}
                onChange={(e) => setForm({ ...form, allow_photo_uploads: e.target.checked })}
                className="w-5 h-5 mt-0.5 rounded border-slate-300 text-indigo-600"
              />
              <div className="flex-1">
                <label htmlFor="allow_photo_uploads" className="text-sm font-medium text-indigo-900 cursor-pointer">
                  Allow Workers to Upload Photos/Images
                </label>
                <p className="text-sm text-indigo-700 mt-1">
                  When enabled, workers can attach photos as proof of task completion. You will see these images in the Tasks Needing Review section.
                </p>
                {form.allow_photo_uploads && (
                  <div className="mt-2 p-2 bg-white rounded-lg border border-indigo-200">
                    <p className="text-xs text-indigo-600 flex items-center gap-2">
                      <Camera className="w-4 h-4" />
                      Workers can upload up to 5 images per task submission
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ========== TASK VERIFICATION REQUIRED ========== */}
          <div className="p-5 bg-amber-50 rounded-xl border border-amber-200">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Task Verification Required</p>
                <p className="text-sm text-amber-700 mt-1">
                  A staff member from the program or assigned work location must verify if all tasks are completed.
                  The verifying staff will be required to provide their <strong>name</strong> and <strong>signature</strong> on the worker's portal to confirm task completion.
                </p>
              </div>
            </div>
            
            {/* Manager Acknowledgment Section */}
            <div className="mt-4 pt-4 border-t border-amber-200">
              <p className="text-sm font-semibold text-amber-900 mb-3">Manager Acknowledgment</p>
              
              {/* Status Badge */}
              {form.task_verification?.requirement_accepted !== null && (
                <div className={`mb-4 p-3 rounded-lg ${
                  form.task_verification.requirement_accepted 
                    ? 'bg-green-100 border border-green-300' 
                    : 'bg-red-100 border border-red-300'
                }`}>
                  <p className={`text-sm font-medium ${
                    form.task_verification.requirement_accepted ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {form.task_verification.requirement_accepted 
                      ? '✓ Task verification requirement ACCEPTED' 
                      : '✗ Task verification requirement DENIED'}
                  </p>
                  <p className="text-xs text-slate-600 mt-1">
                    Signed by: {form.task_verification.manager_name}
                  </p>
                </div>
              )}
              
              {/* Name and Signature Fields */}
              <div className="space-y-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-amber-800 mb-1">Manager Name *</label>
                  <input
                    type="text"
                    value={form.task_verification?.manager_name || ''}
                    onChange={(e) => setForm({
                      ...form,
                      task_verification: {
                        ...form.task_verification,
                        manager_name: e.target.value,
                        requirement_accepted: null
                      }
                    })}
                    disabled={form.task_verification?.requirement_accepted !== null}
                    className={`w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 bg-white text-slate-900 ${
                      form.task_verification?.requirement_accepted !== null ? 'bg-slate-100 cursor-not-allowed' : ''
                    }`}
                    placeholder="Enter your full name"
                  />
                </div>
                
                {/* E-Signature Section */}
                <div>
                  <label className="block text-sm font-medium text-amber-800 mb-2">E-Signature *</label>
                  
                  {form.task_verification?.requirement_accepted !== null ? (
                    <div className={`p-4 rounded-lg border-2 ${
                      form.task_verification.requirement_accepted 
                        ? 'bg-green-50 border-green-300' 
                        : 'bg-red-50 border-red-300'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className={`w-4 h-4 ${
                          form.task_verification.requirement_accepted ? 'text-green-600' : 'text-red-600'
                        }`} />
                        <span className={`text-sm font-medium ${
                          form.task_verification.requirement_accepted ? 'text-green-800' : 'text-red-800'
                        }`}>
                          Signature Saved & Locked
                        </span>
                      </div>
                      {form.task_verification.manager_signature?.startsWith('data:') ? (
                        <img 
                          src={form.task_verification.manager_signature} 
                          alt="Saved signature" 
                          className="max-h-16 border border-slate-200 rounded bg-white p-2"
                        />
                      ) : (
                        <p className="text-xl text-blue-800 bg-white p-3 rounded border border-slate-200" 
                           style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }}>
                          {form.task_verification.manager_signature}
                        </p>
                      )}
                      <p className="text-xs text-slate-500 mt-2">
                        Signed on: {new Date(form.task_verification.verified_at).toLocaleString()}
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Type/Draw Toggle */}
                      <div className="flex mb-3 bg-amber-100 rounded-lg p-1">
                        <button
                          type="button"
                          onClick={() => setSignatureMode('type')}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition ${
                            signatureMode === 'type' 
                              ? 'bg-white text-amber-800 shadow-sm' 
                              : 'text-amber-600 hover:text-amber-800'
                          }`}
                        >
                          <Type className="w-4 h-4" />
                          Type
                        </button>
                        <button
                          type="button"
                          onClick={() => setSignatureMode('draw')}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition ${
                            signatureMode === 'draw' 
                              ? 'bg-white text-amber-800 shadow-sm' 
                              : 'text-amber-600 hover:text-amber-800'
                          }`}
                        >
                          <Pen className="w-4 h-4" />
                          Draw
                        </button>
                      </div>
                      
                      {/* Type Signature */}
                      {signatureMode === 'type' && (
                        <div className="relative">
                          <input
                            type="text"
                            value={typedSignature}
                            onChange={(e) => {
                              setTypedSignature(e.target.value);
                              setForm({
                                ...form,
                                task_verification: {
                                  ...form.task_verification,
                                  manager_signature: e.target.value,
                                  requirement_accepted: null
                                }
                              });
                            }}
                            className="w-full px-4 py-4 border-2 border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 bg-white text-2xl text-blue-800"
                            placeholder="Type your signature here"
                            style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive", color: '#1e40af' }}
                          />
                          {typedSignature && (
                            <button
                              type="button"
                              onClick={() => {
                                setTypedSignature('');
                                setForm({
                                  ...form,
                                  task_verification: {
                                    ...form.task_verification,
                                    manager_signature: '',
                                    requirement_accepted: null
                                  }
                                });
                              }}
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      )}
                      
                      {/* Draw Signature */}
                      {signatureMode === 'draw' && (
                        <div className="space-y-2">
                          <div className="relative border-2 border-amber-300 rounded-lg bg-white overflow-hidden" style={{ maxWidth: '100%' }}>
                            <canvas
                              ref={canvasRef}
                              width={350}
                              height={80}
                              onMouseDown={startDrawing}
                              onMouseMove={draw}
                              onMouseUp={stopDrawing}
                              onMouseLeave={stopDrawing}
                              onTouchStart={startDrawing}
                              onTouchMove={draw}
                              onTouchEnd={stopDrawing}
                              className="w-full cursor-crosshair touch-none"
                              style={{ touchAction: 'none', maxHeight: '80px' }}
                            />
                            <div className="absolute bottom-1 left-3 text-xs text-slate-400 pointer-events-none">
                              Sign here
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 h-px bg-slate-300 mx-4 mb-4"></div>
                          </div>
                          <button
                            type="button"
                            onClick={clearCanvas}
                            className="text-sm text-amber-600 hover:text-amber-800 flex items-center gap-1"
                          >
                            <RefreshCw className="w-3 h-3" />
                            Clear signature
                          </button>
                        </div>
                      )}
                      
                      {/* Signature Preview */}
                      {form.task_verification?.manager_signature && signatureMode === 'type' && (
                        <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <p className="text-xs text-slate-500 mb-1">Signature Preview:</p>
                          <p className="text-xl text-blue-800" style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }}>
                            {form.task_verification.manager_signature}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              
              {/* Accept / Deny Buttons */}
              {form.task_verification?.requirement_accepted === null && (
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      let signature = form.task_verification?.manager_signature || '';
                      if (signatureMode === 'draw' && canvasRef.current) {
                        signature = canvasRef.current.toDataURL();
                      }
                      
                      if (!form.task_verification?.manager_name) {
                        toast.error('Please enter your name first');
                        return;
                      }
                      if (!signature || (signatureMode === 'draw' && signature === 'data:,')) {
                        toast.error('Please provide your signature first');
                        return;
                      }
                      
                      setForm({
                        ...form,
                        task_verification: {
                          ...form.task_verification,
                          manager_signature: signature,
                          requirement_accepted: true,
                          verified_at: new Date().toISOString()
                        }
                      });
                      toast.success('Task verification requirement accepted');
                    }}
                    className="flex-1 px-4 py-2.5 rounded-lg font-medium transition flex items-center justify-center gap-2 bg-green-500 text-white hover:bg-green-600"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Accept Requirement
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      let signature = form.task_verification?.manager_signature || '';
                      if (signatureMode === 'draw' && canvasRef.current) {
                        signature = canvasRef.current.toDataURL();
                      }
                      
                      if (!form.task_verification?.manager_name) {
                        toast.error('Please enter your name first');
                        return;
                      }
                      if (!signature || (signatureMode === 'draw' && signature === 'data:,')) {
                        toast.error('Please provide your signature first');
                        return;
                      }
                      
                      setForm({
                        ...form,
                        task_verification: {
                          ...form.task_verification,
                          manager_signature: signature,
                          requirement_accepted: false,
                          verified_at: new Date().toISOString()
                        }
                      });
                      toast.info('Task verification requirement denied');
                    }}
                    className="flex-1 px-4 py-2.5 rounded-lg font-medium transition flex items-center justify-center gap-2 bg-red-500 text-white hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                    Deny Requirement
                  </button>
                </div>
              )}
              
              <p className="text-xs text-amber-600 mt-3">
                * By signing, you acknowledge understanding of the task verification policy for this shift.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-slate-600 hover:bg-slate-100 rounded-xl transition">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 bg-amber-600 text-white rounded-xl font-medium hover:bg-amber-700 transition disabled:opacity-50 flex items-center gap-2"
            >
              {loading && <RefreshCw className="w-4 h-4 animate-spin" />}
              Post Shift
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


export default CreateShiftModal;
